import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import Input from '@jetbrains/ring-ui/components/input/input'
import Select from '@jetbrains/ring-ui/components/select/select'
import classNames from 'classnames'
import type {Duration} from 'moment'
import moment from 'moment'
import * as React from 'react'
import type {SyntheticEvent} from 'react'

import {useAppSelector} from '../../../../../hooks/react-redux'
import {useSlice} from '../../../../../hooks/useSlice'
import {getIsAgentTypeManageable} from '../../../../../selectors'
import type {EnabledInfo} from '../../../../../services/rest'
import {restApi} from '../../../../../services/rest'
import CommonForm from '../CommonForm/CommonForm'
import CommonPopup from '../CommonPopup/CommonPopup'

import type {ActionProps, FormProps, PopupAttrs} from './EnableAgentForm.types'
import {getAction} from './EnableAgentForm.utils'
import EnableComment from './EnableComment'
import EnableTimer from './EnableTimer/EnableTimer'
import EnableToggle from './EnableToggle/EnableToggle'

import styles from './EnableAgentForm.css'

type Item = {
  duration: Duration
  label: string
  key: number
}

/* eslint-disable @typescript-eslint/no-magic-numbers */
const restoreTimers: ReadonlyArray<Item> = [
  moment.duration(15, 'minutes'),
  moment.duration(30, 'minutes'),
  moment.duration(1, 'hour'),
  moment.duration(2, 'hours'),
  moment.duration(4, 'hours'),
  moment.duration(8, 'hours'),
  moment.duration(1, 'day'),
  moment.duration(2, 'days'),
  moment.duration(5, 'days'),
].map(duration => ({
  label: duration.humanize(),
  key: duration.asMinutes(),
  duration,
}))

const EnableAgentPopupView: React.ComponentType<PopupAttrs & ActionProps> = React.memo(
  function EnableAgentPopupView({
    name = '',
    enabled,
    onClose,
    onToggle,
    hidden,
  }: PopupAttrs & ActionProps) {
    const [comment, setComment] = React.useState('')
    const [restore, setRestore] = React.useState(false)
    const [restoreTimer, setRestoreTimer] = React.useState(restoreTimers[0])
    const formRef = React.useRef()
    const onSubmit = React.useCallback(
      () =>
        onToggle({
          comment: {
            text: comment,
          },
          statusSwitchTime: restore ? `+${restoreTimer.duration.asMinutes()}m` : undefined,
          status: !enabled,
        }).then(() => {
          setComment('')
          setRestore(false)
          setRestoreTimer(restoreTimers[0])
        }),
      [onToggle, comment, restore, restoreTimer, enabled],
    )
    const handleCommentChange = React.useCallback(
      (e: SyntheticEvent<HTMLTextAreaElement>) => {
        setComment(e.currentTarget.value)
      },
      [setComment],
    )
    const handleRestoreChange = React.useCallback(
      (e: SyntheticEvent<HTMLInputElement>) => setRestore(e.currentTarget.checked),
      [setRestore],
    )
    const handleRestoreTimerSelect = React.useCallback(
      (selected: Item | null) => selected && setRestoreTimer(selected),
      [setRestoreTimer],
    )
    return (
      <CommonPopup
        hidden={hidden}
        header={`${getAction(enabled)} ${name}`}
        content={
          <CommonForm
            content={
              <>
                <Input
                  autoFocus
                  className={styles.comment}
                  inputClassName="submit-on-cmd-enter"
                  label="Comment"
                  placeholder="Add an explanation for colleagues"
                  multiline
                  value={comment}
                  onChange={handleCommentChange}
                />
                <Checkbox checked={restore} onChange={handleRestoreChange}>
                  {`${getAction(!enabled)} automatically in`}
                </Checkbox>
                <Select
                  className={styles.restoreTimes}
                  popupClassName={styles.timeoutSelect}
                  type={Select.Type.INLINE}
                  data={restoreTimers}
                  selected={restoreTimer}
                  onSelect={handleRestoreTimerSelect}
                />
              </>
            }
            formRef={formRef}
            onSubmit={onSubmit}
            onSuccess={onClose}
            submitButtonContent={getAction(enabled)}
            onClose={onClose}
          />
        }
        onClose={onClose}
      />
    )
  },
)
function EnableAgentPopup(props: PopupAttrs & Partial<ActionProps>) {
  const [setEnabledInfo] = restApi.endpoints.setEnabledInfo.useMutation()
  const onToggle = React.useCallback(
    (enabledInfo: EnabledInfo) => setEnabledInfo({agentLocator: `id:${props.id}`, enabledInfo}),
    [props.id, setEnabledInfo],
  )
  return <EnableAgentPopupView {...props} onToggle={props.onToggle ?? onToggle} />
}
const EnableAgentForm: React.ComponentType<FormProps & Partial<ActionProps>> = React.memo(
  function EnableAgentForm({
    className,
    toggleClassName,
    id,
    name,
    enabled,
    authorized,
    commentText,
    commentTimestamp,
    commentUser,
    statusSwitchTime,
    poolId,
    typeId,
    onToggle,
    size,
    pale,
  }: FormProps & Partial<ActionProps>) {
    const {isToggleable} = restApi.endpoints.getPoolPermissions.useQuery(undefined, {
      selectFromResult: ({data}) => ({
        isToggleable: poolId != null && (data?.[poolId]?.canChangeStatus ?? false),
      }),
    })
    const isManageable = useAppSelector(state => getIsAgentTypeManageable(state, typeId))

    const [show, actions] = useSlice(
      {
        toggle: (prevShow, _: void) => !prevShow,
        close: (_, __: void) => false,
      },
      false,
    )
    const toggle = React.useMemo(
      () => (
        <EnableToggle
          className={toggleClassName}
          enabled={enabled}
          handleToggleClick={actions.toggle}
          isToggleable={isToggleable && isManageable}
          size={size}
          pale={pale}
        />
      ),
      [actions.toggle, enabled, isManageable, isToggleable, pale, size, toggleClassName],
    )
    return (
      <>
        <div className={classNames(styles.agentToggle, className)}>
          {authorized && statusSwitchTime != null ? (
            <EnableTimer
              enabled={enabled}
              statusSwitchTime={statusSwitchTime}
              iconClassName={styles.timerIcon}
            />
          ) : null}
          {commentText == null ? (
            toggle
          ) : (
            <EnableComment
              enabled={enabled}
              commentText={commentText}
              commentTimestamp={commentTimestamp}
              commentUser={commentUser}
              anchor={toggle}
            />
          )}
        </div>
        <EnableAgentPopup
          hidden={!show}
          id={id}
          name={name}
          enabled={enabled}
          onClose={actions.close}
          onToggle={onToggle}
        />
      </>
    )
  },
)
export {EnableAgentPopup, EnableAgentPopupView, EnableAgentForm}
